import { useEffect, useRef } from "react";
import { collection, onSnapshot, orderBy, query, where, limit } from "firebase/firestore";
import { db } from "./firebase";
import { useAuth } from "./auth";

type CashierPurchase = {
  amountFinal: number;
  amountDiscount: number;
  category: string | null;
};

/**
 * Detecta cuando un cajero registra una venta nueva para el user actual.
 * Llama al callback con los datos para mostrar toast in-app.
 */
export function useCashierPurchaseNotifier(onNewPurchase: (p: CashierPurchase) => void) {
  const { user, claims } = useAuth();
  const seen = useRef<Set<string>>(new Set());
  const initialized = useRef(false);

  useEffect(() => {
    if (!user || !claims.tenantId) return;
    seen.current = new Set();
    initialized.current = false;

    const q = query(
      collection(db, "tenants", claims.tenantId, "purchases"),
      where("userId", "==", user.uid),
      where("source", "==", "cashier_assisted"),
      orderBy("createdAt", "desc"),
      limit(5)
    );
    return onSnapshot(q, (snap) => {
      if (!initialized.current) {
        snap.docs.forEach((d) => seen.current.add(d.id));
        initialized.current = true;
        return;
      }
      snap.docChanges().forEach((ch) => {
        if (ch.type !== "added") return;
        if (seen.current.has(ch.doc.id)) return;
        seen.current.add(ch.doc.id);
        const d = ch.doc.data();
        onNewPurchase({
          amountFinal: d.amountFinal ?? d.amount ?? 0,
          amountDiscount: d.amountDiscount ?? 0,
          category: d.category ?? null
        });
      });
    });
  }, [user, claims.tenantId, onNewPurchase]);
}
